import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';

const Login: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (isLogin) {
        const success = await login(email, password);
        if (success) {
          navigate('/');
        } else {
          setError('Invalid email or password');
        }
      } else {
        if (!fullName.trim()) {
          setError('Full name is required');
          setIsLoading(false);
          return;
        }
        const result = await register(email, password, fullName);
        if (result) {
          navigate('/verify-otp', { state: { email, purpose: 'verification' } });
        } else {
          setError('Registration failed. Please try again.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="split-layout">
      <div className="split-layout-left">
        <div className="branding">
          <div className="branding-logo">
            <h1>MavScope</h1>
            <p>AI-Powered Roof & Siding Measurements</p>
          </div>
          
          <div className="branding-features">
            <div className="branding-feature">
              <div className="branding-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              </div>
              <div className="branding-feature-text">
                <h3>AI-Powered Analysis</h3>
                <p>Advanced computer vision delivers accurate roof and siding measurements from satellite imagery.</p>
              </div>
            </div>
            
            <div className="branding-feature">
              <div className="branding-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <div className="branding-feature-text">
                <h3>Professional Reports</h3>
                <p>Generate detailed PDF reports with measurements, material estimates, and diagrams.</p>
              </div>
            </div>
            
            <div className="branding-feature">
              <div className="branding-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <div className="branding-feature-text">
                <h3>Fast Turnaround</h3>
                <p>Get comprehensive measurement reports in minutes instead of days.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="split-layout-right">
        <div style={{ width: '100%', maxWidth: '400px' }}>
          <div className="mobile-logo">
            <h1>MavScope</h1>
            <p>AI-Powered Measurements</p>
          </div>
          
          <div className="form-card">
            <div className="form-header">
              <h2 className="form-title">{isLogin ? 'Welcome back' : 'Create account'}</h2>
              <p className="form-description">
                {isLogin 
                  ? 'Sign in to access your measurement dashboard' 
                  : 'Start measuring properties in minutes'}
              </p>
            </div>

            <div className="tabs" style={{ marginBottom: 'var(--spacing-6)' }}>
              <button 
                className={`tab ${isLogin ? 'tab-active' : ''}`}
                onClick={() => setIsLogin(true)}
                type="button"
              >
                Sign In
              </button>
              <button 
                className={`tab ${!isLogin ? 'tab-active' : ''}`}
                onClick={() => setIsLogin(false)}
                type="button"
              >
                Register
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-fields">
                {!isLogin && (
                  <Input
                    label="Full Name"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter your full name"
                    required={!isLogin}
                    autoComplete="name"
                  />
                )}
                
                <Input
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  autoComplete="email"
                />

                <Input
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                />

                {error && <Alert variant="error">{error}</Alert>}
              </div>

              <div className="form-actions">
                <Button 
                  type="submit" 
                  variant="primary"
                  size="lg"
                  fullWidth
                  isLoading={isLoading}
                >
                  {isLogin ? 'Sign In' : 'Create Account'}
                </Button>
              </div>
            </form>

            {isLogin && (
              <div style={{ textAlign: 'center', marginTop: 'var(--spacing-4)' }}>
                <Link to="/forgot-password" className="link" style={{ fontSize: 'var(--text-sm)' }}>
                  Forgot your password?
                </Link>
              </div>
            )}

            <div className="form-footer">
              <p>By continuing, you agree to our Terms of Service and Privacy Policy.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
